import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { HeartHandshake, IndianRupee, Package, Download, TrendingUp } from "lucide-react";

export default function Donations() {
  const { currentUser } = useAuth();
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const q = query(collection(db, "donations"), where("userId","==",currentUser.uid), orderBy("date","desc"));
        const snap = await getDocs(q);
        setDonations(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    }
    if (currentUser) load();
  }, [currentUser]);

  const totalMoney = donations.filter(d=>d.type==="money").reduce((a,b)=>a+(b.amount||0),0);
  const itemDrives = donations.filter(d=>d.type==="items").length;
  const verified   = donations.filter(d=>d.verified).length;

  const TYPE_ICON = {
    money: { bg:"rgba(34,197,94,0.12)", color:"#22c55e" },
    items: { bg:"rgba(245,158,11,0.12)", color:"#f59e0b" },
  };

  return (
    <div style={{maxWidth:800}}>
      {/* Stats */}
      <div style={s.statsRow}>
        {[
          {label:"Total Donated",  value:`₹${totalMoney.toLocaleString()}`, sub:"cash contributions", color:"#22c55e", icon:IndianRupee},
          {label:"Item Drives",    value:itemDrives, sub:"drives participated", color:"#f59e0b", icon:Package},
          {label:"Verified",       value:verified,   sub:"confirmed by admin", color:"#3b82f6", icon:TrendingUp},
          {label:"All Records",    value:donations.length, sub:"total entries", color:"#a855f7", icon:HeartHandshake},
        ].map(({label,value,sub,color,icon:Icon})=>(
          <div key={label} style={s.statCard}>
            <div style={{...s.statIcon,background:`${color}18`}}><Icon size={18} color={color}/></div>
            <div style={{...s.statVal,color}}>{value}</div>
            <div style={s.statLabel}>{label}</div>
            <div style={s.statSub}>{sub}</div>
          </div>
        ))}
      </div>

      {/* List */}
      <div style={s.card}>
        <div style={s.cardTitle}>All Donations</div>
        {loading ? <div style={s.empty}>Loading...</div> :
         donations.length === 0 ? (
          <div style={{textAlign:"center",padding:"40px 0"}}>
            <HeartHandshake size={40} color="#52504d" style={{marginBottom:12}}/>
            <div style={{fontSize:14,color:"#52504d",marginBottom:6}}>No donations recorded yet</div>
            <div style={{fontSize:12,color:"#3a3835"}}>Contact admin to log your donations</div>
          </div>
         ) : donations.map(don => {
          const tc = TYPE_ICON[don.type] || TYPE_ICON.items;
          return (
            <div key={don.id} style={s.row}>
              <div style={{...s.typeIcon, background:tc.bg}}>
                {don.type==="money" ? <IndianRupee size={16} color={tc.color}/> : <Package size={16} color={tc.color}/>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={s.rowTitle}>{don.type==="money" ? `Cash Donation` : `Item Donation — ${don.items?.join(", ")}`}</div>
                <div style={s.rowSub}>{don.date} {don.receiptNo ? `· Receipt #${don.receiptNo}` : ""}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                <span style={{...s.badge, background:don.verified?"rgba(34,197,94,0.12)":"rgba(245,158,11,0.12)", color:don.verified?"#22c55e":"#f59e0b"}}>
                  {don.verified ? "✓ Verified" : "Pending"}
                </span>
                <span style={{...s.typeBadge, background:tc.bg, color:tc.color}}>
                  {don.type==="money" ? `₹${don.amount?.toLocaleString()}` : "Items"}
                </span>
                {don.receiptUrl && (
                  <a href={don.receiptUrl} target="_blank" rel="noreferrer" style={s.dlBtn}>
                    <Download size={13}/> Receipt
                  </a>
                )}
              </div>
            </div>
          );
         })}
      </div>

      {/* Note */}
      <div style={s.noteBox}>
        <HeartHandshake size={18} color="#f59e0b"/>
        <div style={{fontSize:13,color:"#78716c",lineHeight:1.6}}>
          All donations are recorded by the admin team. To log a new donation or report a discrepancy,
          contact us at <span style={{color:"#f59e0b"}}>htstrust@gmail.com</span> or call <span style={{color:"#f59e0b"}}>+91 7669990505</span>
        </div>
      </div>
    </div>
  );
}

const s = {
  statsRow: { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:12, marginBottom:16 },
  statCard: { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:16 },
  statIcon: { width:36, height:36, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 },
  statVal: { fontSize:24, fontWeight:800, fontFamily:"'Playfair Display',serif", lineHeight:1, marginBottom:4 },
  statLabel: { fontSize:12, fontWeight:700, color:"#fef3e2", marginBottom:2 },
  statSub: { fontSize:11, color:"#52504d" },
  card: { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20, marginBottom:16 },
  cardTitle: { fontSize:14, fontWeight:700, color:"#fef3e2", marginBottom:16 },
  row: { display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:"1px solid rgba(255,255,255,0.04)", flexWrap:"wrap" },
  typeIcon: { width:38, height:38, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  rowTitle: { fontSize:13, fontWeight:600, color:"#fef3e2" },
  rowSub: { fontSize:11, color:"#52504d", marginTop:2 },
  badge: { fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:50 },
  typeBadge: { fontSize:13, fontWeight:800, padding:"3px 10px", borderRadius:50 },
  dlBtn: { display:"inline-flex", alignItems:"center", gap:4, background:"rgba(59,130,246,0.12)", color:"#3b82f6", border:"1px solid rgba(59,130,246,0.2)", borderRadius:7, padding:"5px 10px", fontSize:11, fontWeight:700, textDecoration:"none" },
  noteBox: { background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.15)", borderRadius:12, padding:16, display:"flex", gap:12, alignItems:"flex-start" },
  empty: { fontSize:13, color:"#52504d", textAlign:"center", padding:"24px 0" },
};
